import { useEffect, useState } from 'react';
import { Apple, Copy, Download, ExternalLink, Loader2, Mail, MessageCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const STEPS = [
  { id: 'android', icon: Smartphone, title: 'Android (Chrome)', body: 'Open the link, tap the ⋮ menu, then "Add to Home screen" / "Install app".' },
  { id: 'ios', icon: Apple, title: 'iPhone (Safari)', body: 'Open the link in Safari, tap the Share button, then "Add to Home Screen".' },
];

export const StaffAppInstallDialog = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [qr, setQr] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    let objectUrl = '';
    setData(null); setQr(''); setError('');
    api.get('/staff-app/install').then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || 'Could not load your staff app link.'); });
    api.get('/staff-app/install/qr.png', { responseType: 'blob' }).then((r) => {
      objectUrl = URL.createObjectURL(r.data);
      if (live) setQr(objectUrl); else URL.revokeObjectURL(objectUrl);
    }).catch(() => {});
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="staff-app-install-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5" /> Staff mobile app</DialogTitle>
          <DialogDescription data-testid="staff-app-install-subtitle">
            {data ? `${data.pharmacy.name} · installs on your phone and signs you in with this same staff account` : 'Install the Staff Companion on your phone.'}
          </DialogDescription>
        </DialogHeader>

        {error && <p role="alert" className="text-sm text-destructive" data-testid="staff-app-install-error">{error}</p>}
        {!data && !error && <Loader2 className="h-5 w-5 animate-spin" data-testid="staff-app-install-loading" />}

        {data && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              {qr
                ? <img src={qr} alt={`${data.pharmacy.name} staff app QR code`} className="w-40 h-40 border bg-white p-2 object-contain" data-testid="staff-app-install-qr" />
                : <div className="w-40 h-40 border grid place-items-center text-xs text-muted-foreground" data-testid="staff-app-install-qr-loading">Preparing QR…</div>}
              <p className="text-xs text-muted-foreground text-center">Scan with your phone camera to open {data.branch_name}'s staff app.</p>
            </div>

            <div className="flex items-center gap-2 border rounded-md p-2 min-w-0">
              <span className="text-xs break-all flex-1 min-w-0" data-testid="staff-app-install-link">{data.link}</span>
              <Button variant="outline" size="icon" title="Copy staff app link" data-testid="staff-app-install-copy"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(data.link); toast.success('Staff app link copied'); }
                  catch { toast.error('Could not copy. Select and copy the link shown here.'); }
                }}><Copy className="h-4 w-4" /></Button>
              <a href={data.link} target="_blank" rel="noreferrer" title="Open staff app" data-testid="staff-app-install-open"
                className="p-2 border rounded-md transition-colors hover:bg-muted"><ExternalLink className="h-4 w-4" /></a>
            </div>

            {qr && <Button variant="outline" size="sm" className="gap-2 w-full" data-testid="staff-app-install-qr-download"
              onClick={() => {
                const anchor = document.createElement('a');
                anchor.href = qr;
                anchor.download = 'staff-app-qr.png';
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                toast.success('QR code downloaded');
              }}><Download className="h-4 w-4" /> Download QR to share with the team</Button>}

            <div className="space-y-3 border-t pt-3" data-testid="staff-app-invite-section">
              <p className="text-sm font-medium">Invite a team member</p>
              <div className="flex gap-2">
                <a className="flex-1" data-testid="staff-app-invite-whatsapp"
                  href={`https://wa.me/?text=${encodeURIComponent(`Install the ${data.pharmacy.name} staff app on your phone and sign in with your own staff account: ${data.link}`)}`}
                  target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" className="gap-2 w-full"><MessageCircle className="h-4 w-4" /> Share on WhatsApp</Button>
                </a>
              </div>
              {data.can_invite ? (
                <div className="flex gap-2">
                  <Input type="email" placeholder="colleague@pharmacy.com" value={email} disabled={sending}
                    onChange={(e) => setEmail(e.target.value)} data-testid="staff-app-invite-email" />
                  <Button size="sm" className="gap-2 shrink-0" disabled={sending || !email.trim()} data-testid="staff-app-invite-send"
                    onClick={async () => {
                      setSending(true);
                      try {
                        await api.post('/staff-app/install/invite', { email: email.trim() });
                        toast.success(`Invite sent to ${email.trim()}`);
                        setEmail('');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Could not send the invite email.');
                      } finally { setSending(false); }
                    }}>
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="staff-app-invite-no-permission">
                  Ask your pharmacy admin to email invites to the rest of the team.
                </p>
              )}
            </div>

            <div className="space-y-3 border-t pt-3" data-testid="staff-app-install-steps">
              {STEPS.map(({ id, icon: Icon, title, body }) => (
                <div key={id} className="flex gap-3" data-testid={`staff-app-install-step-${id}`}>
                  <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <div><p className="text-sm font-medium">{title}</p><p className="text-xs text-muted-foreground">{body}</p></div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Everyone signs in with their own staff account, so each person only sees what their role allows.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
